import React, { useState } from 'react';
import {
  OAuthCredentialSummaryDto,
  OAuthProviderType,
  tenancyApi,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Badge, formatDateTime, formatRelativeTime } from '../../components/ui/Badge';
import { IconLink, IconSync } from '../../components/ui/Icons';

interface ProviderCardConfig {
  provider: OAuthProviderType;
  title: string;
  subtitle: string;
  accentColor: string;
  badgeLabel: string;
  description: string;
}

const PROVIDERS: ProviderCardConfig[] = [
  {
    provider: 'procore',
    title: 'Procore Construction Cloud',
    subtitle: 'Source of Truth for Projects, Schedules & Tasks',
    accentColor: 'bg-[#f45800]',
    badgeLabel: 'Primary Construction ERP',
    description:
      'Required to discover Procore companies & projects, synchronize schedule tasks, upload Gantt PDF exports, and register Procore project webhooks.',
  },
  {
    provider: 'smartsheet',
    title: 'Smartsheet Collaborative Work Management',
    subtitle: 'Target Sheets, Workspaces, Gantt Columns & Real-Time Webhooks',
    accentColor: 'bg-[#003057]',
    badgeLabel: 'Schedule & Sheet Target',
    description:
      'Connect your Smartsheet account to list or create sheets, map columns, sync task hierarchies, and receive real-time cell change webhooks.',
  },
];

export function ConnectionsPage() {
  const { user, refreshUserSession } = useAuth();
  const { pushToast } = useToast();

  const [removingProvider, setRemovingProvider] = useState<OAuthProviderType | null>(null);

  const getCredentialForProvider = (
    provider: OAuthProviderType,
  ): OAuthCredentialSummaryDto | null => {
    if (!user) return null;
    if (provider === 'procore' && user.procore_oauth) return user.procore_oauth;
    if (provider === 'smartsheet' && user.smartsheet_oauth) return user.smartsheet_oauth;
    return user.oauth_credentials?.find((c) => c.provider === provider) || null;
  };

  const handleOAuthRedirect = (provider: OAuthProviderType) => {
    window.location.href = tenancyApi.getOAuthStartUrl(provider);
  };

  const handleDisconnectProvider = async (provider: OAuthProviderType) => {
    setRemovingProvider(provider);
    try {
      await tenancyApi.removeOAuthProvider(provider);
      await refreshUserSession();
      pushToast({
        tone: 'success',
        title: `Disconnected ${provider}`,
        description: `OAuth credentials for ${provider} were removed.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: `Failed to disconnect ${provider}`,
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setRemovingProvider(null);
    }
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Connections &amp; Integrations
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Authorize your Procore and Smartsheet OAuth 2.0 credentials so Sync can read and write
            project schedules and tasks on your behalf.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refreshUserSession()}
          className="inline-flex items-center gap-2 self-start rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-2xs"
        >
          <IconSync className="w-4 h-4" />
          <span>Refresh Connection Status</span>
        </button>
      </div>

      {/* OAuth Provider Cards Grid */}
      <section aria-labelledby="oauth-heading" className="space-y-4">
        <h2 id="oauth-heading" className="text-base font-semibold text-slate-900">
          Cloud OAuth 2.0 Providers
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {PROVIDERS.map((cfg) => {
            const cred = getCredentialForProvider(cfg.provider);
            const isConnected = Boolean(cred?.connected && cred.status === 'active');

            return (
              <article
                key={cfg.provider}
                className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs"
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-xl text-white font-bold text-sm shadow-xs ${cfg.accentColor}`}
                      >
                        {cfg.provider.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900">{cfg.title}</h3>
                        <span className="text-[11px] text-slate-500">{cfg.badgeLabel}</span>
                      </div>
                    </div>

                    {isConnected ? (
                      <Badge variant="success" dot>
                        Connected
                      </Badge>
                    ) : cred ? (
                      <Badge variant="warning" dot>
                        {cred.status}
                      </Badge>
                    ) : (
                      <Badge variant="neutral">Not Connected</Badge>
                    )}
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed">{cfg.description}</p>

                  {cred && (
                    <dl className="rounded-xl bg-slate-50 p-3.5 text-xs space-y-1.5 border border-slate-100">
                      <div className="flex justify-between gap-2">
                        <dt className="text-slate-500">Account:</dt>
                        <dd className="font-medium text-slate-800 truncate">
                          {cred.account_email || user?.email || 'Authorized Account'}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt className="text-slate-500">Issued:</dt>
                        <dd className="text-slate-700">{formatRelativeTime(cred.issued_at)}</dd>
                      </div>
                      {cred.expires_at && (
                        <div className="flex justify-between gap-2">
                          <dt className="text-slate-500">Expires:</dt>
                          <dd className="text-slate-700">{formatDateTime(cred.expires_at)}</dd>
                        </div>
                      )}
                    </dl>
                  )}
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleOAuthRedirect(cfg.provider)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors"
                  >
                    <IconLink className="w-3.5 h-3.5" />
                    <span>{isConnected ? 'Reconnect OAuth' : `Connect ${cfg.provider}`}</span>
                  </button>

                  {cred && (
                    <button
                      type="button"
                      disabled={removingProvider === cfg.provider}
                      onClick={() => handleDisconnectProvider(cfg.provider)}
                      className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                    >
                      {removingProvider === cfg.provider ? 'Removing…' : 'Disconnect'}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
